import React from 'react';
import { Section } from './ui/Section';
import { Check, X } from 'lucide-react';
export function FutbolTacticoPro() {
  const comparison = [{
    sit: 'Rival que presiona alto',
    sin: 'Improvisás la salida o pateás largo sin criterio',
    con: 'Protocolo exacto de salida jugada según tu sistema'
  }, {
    sit: 'Equipo que defiende en bloque bajo',
    sin: 'No sabés cómo generar espacios',
    con: '8 variantes de ataque posicional listas para usar'
  }, {
    sit: 'Perder al jugador clave en el partido',
    sin: 'Cambios reactivos sin plan',
    con: 'Árbol de decisión táctico pre-armado'
  }, {
    sit: 'Partido trabado en el segundo tiempo',
    sin: 'Intuición o suerte',
    con: '12 modificaciones tácticas por situación de partido'
  }, {
    sit: 'Analizar al próximo rival',
    sin: 'No sabés qué mirar ni cómo tomarlo',
    con: 'Protocolo de observación y hoja de scouting lista'
  }, {
    sit: 'Definir tu modelo de juego',
    sin: 'Copiás lo que viste sin adaptarlo',
    con: 'Guía para construir tu identidad táctica propia'
  }];
  const modules = [{
    icon: '⚽',
    title: 'Módulo 1 — Sistemas de Juego Avanzados',
    desc: 'Análisis profundo del 4-3-3, 4-4-2, 3-5-2, 4-2-3-1 y 5-3-2. Fortalezas, debilidades, variantes dentro del partido y cómo entrenar cada sistema con ejercicios específicos. Para instructores que quieren entender el fútbol, no solo imitarlo.'
  }, {
    icon: '🧠',
    title: 'Módulo 2 — Análisis del Rival',
    desc: 'Protocolo completo de observación y scouting sin tecnología cara. Hoja de análisis lista para completar, puntos de atención defensivos y ofensivos, y cómo transmitirle la información a tus jugadores de forma simple y clara.'
  }, {
    icon: '🔄',
    title: 'Módulo 3 — Gestión Táctica del Partido',
    desc: '50 situaciones de partido resueltas: qué hacer cuando ganás, perdés, empatás, cuando el rival cambia el sistema, cuando un expulsado cambia todo, cuando hay que defender el resultado. Decisiones pre-pensadas para no improvisar en el banco.'
  }, {
    icon: '📋',
    title: 'Módulo 4 — Construcción de tu Modelo de Juego',
    desc: 'Guía paso a paso para definir tu identidad táctica como instructor. Estilo de juego, principios no negociables, forma de defender y atacar. El modelo de juego que hace que tu equipo sea reconocible aunque cambien los jugadores.'
  }, {
    icon: '🗓️',
    title: 'Módulo 5 — Semana Ideal de Partido',
    desc: 'Cómo organizar los 5 días previos al partido según el objetivo táctico de la semana. Qué trabajar lunes, miércoles, viernes y cómo preparar el día anterior. Protocolo completo del día del partido: entrada en calor, charla técnica y cierre.'
  }, {
    icon: '🚨',
    title: 'Módulo 6 — Herramientas de Dirección Técnica',
    desc: 'Planilla de rotaciones y titulares. Protocolo de charla técnica pre y post partido. Guía de comunicación con jugadores en momentos de tensión. Todo lo que un director técnico profesional tiene resuelto de antemano.'
  }];
  return <Section className="bg-white" data-id="element-184">
      <div className="text-center max-w-4xl mx-auto mb-6 md:mb-12" data-id="element-185">
        <h2 className="text-2xl md:text-5xl font-black text-dark mb-3 md:mb-6" data-id="element-186">
          ¿QUÉ AGREGA EL FÚTBOL TÁCTICO PRO™?
        </h2>
        <p className="text-sm md:text-xl text-gray-700 font-medium" data-id="element-187">
          El sistema de análisis que hace que ningún rival te sorprenda y
          ninguna situación de partido te encuentre sin respuesta
        </p>
      </div>

      {/* Comparison Table */}
      <div className="max-w-5xl mx-auto mb-10 md:mb-20" data-id="element-188">
        {/* Desktop Table */}
        <div className="hidden md:block overflow-hidden rounded-xl border border-gray-200 shadow-sm" data-id="element-189">
          <table className="w-full text-left border-collapse" data-id="element-190">
            <thead data-id="element-191">
              <tr data-id="element-192">
                <th className="p-4 bg-gray-100 font-bold text-dark w-1/3 text-base" data-id="element-193">
                  Situación
                </th>
                <th className="p-4 bg-red-50 font-bold text-red-700 w-1/3 text-base border-l border-r border-gray-200" data-id="element-194">
                  Sin Fútbol Táctico Pro
                </th>
                <th className="p-4 bg-green-50 font-bold text-primary w-1/3 text-base" data-id="element-195">
                  Con Fútbol Táctico Pro
                </th>
              </tr>
            </thead>
            <tbody data-id="element-196">
              {comparison.map((row, i) => <tr key={i} className="border-b border-gray-100" data-id="element-197">
                  <td className="p-4 font-bold text-gray-800 text-base bg-gray-50" data-id="element-198">
                    {row.sit}
                  </td>
                  <td className="p-4 text-gray-700 text-base border-l border-r border-gray-100" data-id="element-199">
                    <div className="flex items-start" data-id="element-200">
                      <X className="w-5 h-5 text-red-500 mr-1.5 flex-shrink-0 mt-0.5" data-id="element-201" />
                      <span data-id="element-202">{row.sin}</span>
                    </div>
                  </td>
                  <td className="p-4 text-dark font-semibold bg-green-50/30 text-base" data-id="element-203">
                    <div className="flex items-start" data-id="element-204">
                      <Check className="w-5 h-5 text-primary mr-1.5 flex-shrink-0 mt-0.5" data-id="element-205" />
                      <span data-id="element-206">{row.con}</span>
                    </div>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>

        {/* Mobile Cards */}
        <div className="md:hidden space-y-4" data-id="element-207">
          {comparison.map((row, i) => <div key={i} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden" data-id="element-208">
              <div className="bg-gray-100 p-3 font-bold text-gray-800 text-sm border-b border-gray-200" data-id="element-209">
                {row.sit}
              </div>
              <div className="p-3 border-b border-gray-100" data-id="element-210">
                <div className="flex items-center text-red-700 font-bold text-xs mb-1.5" data-id="element-211">
                  <X className="w-3.5 h-3.5 mr-1" data-id="element-212" /> SIN FÚTBOL TÁCTICO PRO
                </div>
                <div className="text-gray-700 text-sm font-medium pl-4.5" data-id="element-213">
                  {row.sin}
                </div>
              </div>
              <div className="p-3 bg-green-50/30" data-id="element-214">
                <div className="flex items-center text-primary font-bold text-xs mb-1.5" data-id="element-215">
                  <Check className="w-3.5 h-3.5 mr-1" data-id="element-216" /> CON FÚTBOL TÁCTICO PRO
                </div>
                <div className="text-dark text-sm font-bold pl-4.5" data-id="element-217">
                  {row.con}
                </div>
              </div>
            </div>)}
        </div>
      </div>

      {/* Modules Grid */}
      <div className="max-w-6xl mx-auto" data-id="element-218">
        <h3 className="text-lg md:text-2xl font-black text-center mb-4 md:mb-10 text-dark" data-id="element-219">
          ✅ FÚTBOL TÁCTICO PRO™ INCLUYE:
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-6" data-id="element-220">
          {modules.map((mod, i) => <div key={i} className="bg-gray-50 p-4 md:p-6 rounded-xl md:rounded-2xl border border-gray-200 hover:border-primary/50 transition-colors" data-id="element-221">
              <div className="text-2xl md:text-4xl mb-2 md:mb-4" data-id="element-222">
                {mod.icon}
              </div>
              <h4 className="text-sm md:text-lg font-black text-dark mb-1.5 md:mb-3" data-id="element-223">
                {mod.title}
              </h4>
              <p className="text-gray-700 text-xs md:text-sm leading-relaxed font-medium" data-id="element-224">
                {mod.desc}
              </p>
            </div>)}
        </div>
      </div>
    </Section>;
}